import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, ZONES, PROPERTY_TYPES, formatAED, UAE_EMIRATES } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { useT } from "../lib/i18n";
import { toast } from "sonner";
import useSEO from "../lib/useSEO";
import { breadcrumbLd, itemListLd } from "../lib/jsonld";
import { trackFilterApply, trackFilterSave, trackFilterLoad } from "../lib/searchAnalytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const SAVED_KEY = "bw_saved_searches";

function loadSavedSearches() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"); } catch { return []; }
}
function persistSavedSearches(arr) {
  localStorage.setItem(SAVED_KEY, JSON.stringify(arr));
}
function describeFilters(f) {
  const parts = [];
  if (f.emirate) parts.push(f.emirate);
  if (f.zone) parts.push(f.zone);
  if (f.property_type) {
    const pt = PROPERTY_TYPES.find((p) => p.value === f.property_type);
    parts.push(pt?.label || f.property_type);
  }
  if (f.min_size || f.max_size) parts.push(`${f.min_size || "—"}-${f.max_size || "—"} sqft`);
  if (f.min_price || f.max_price) parts.push(`${f.min_price || "—"}-${f.max_price || "—"} AED`);
  if (f.search) parts.push(`"${f.search}"`);
  return parts.join(" · ") || "All";
}

export default function Properties() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [aiReason, setAiReason] = React.useState("");
  const [filters, setFilters] = React.useState({
    zone: params.get("zone") || "",
    property_type: params.get("type") || params.get("property_type") || "",
    rent_sale: params.get("rent_sale") || "",
    emirate: params.get("emirate") || "",
    min_size: params.get("min_size") || "",
    max_size: params.get("max_size") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    search: params.get("search") || "",
  });

  const aiQuery = params.get("ai_query") || "";

  const filtersKey = JSON.stringify(filters);

  /* eslint-disable */
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // AI-driven query takes precedence — call the smart-search endpoint
    if (aiQuery) {
      api.post("/ai/search", { query: aiQuery, lang: "en" }).then((res) => {
        if (cancelled) return;
        setProperties(res.data.matches || []);
        setAiReason(res.data.reasoning || "");
        setLoading(false);
      }).catch(() => {
        if (cancelled) return;
        toast.error("AI search failed — showing all listings");
        setAiReason("");
        api.get("/properties").then((r) => { if (!cancelled) { setProperties(r.data); setLoading(false); } });
      });
      return () => { cancelled = true; };
    }
    setAiReason("");
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/properties?${q.toString()}`).then((res) => {
      if (cancelled) return;
      setProperties(res.data);
      setLoading(false);
    });
    trackFilterApply(filters);
    const qs = q.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    return () => { cancelled = true; };
  }, [filtersKey, aiQuery]);
  /* eslint-enable */

  const reset = () => {
    setFilters({ zone: "", property_type: "", rent_sale: "", emirate: "", min_size: "", max_size: "", min_price: "", max_price: "", search: "" });
    setParams({});
  };

  const clearAi = () => setParams({});

  const setRentSale = (v) => setFilters((f) => ({ ...f, rent_sale: v }));

  const [saved, setSaved] = React.useState(loadSavedSearches);
  const [saveDialog, setSaveDialog] = React.useState({ open: false, name: "" });
  const currentDesc = describeFilters(filters);
  const isEmpty = Object.values(filters).every((v) => !v);
  const matchingExisting = saved.find((s) => JSON.stringify(s.filters) === JSON.stringify(filters));

  const openSaveDialog = () => {
    if (matchingExisting || isEmpty) return;
    const defaultName = currentDesc.length > 32 ? currentDesc.slice(0, 32) + "…" : currentDesc;
    setSaveDialog({ open: true, name: defaultName });
  };

  const confirmSave = () => {
    const name = saveDialog.name.trim() || t("saved.default_name");
    const next = [...saved, { id: Date.now().toString(), name, filters: { ...filters } }];
    setSaved(next);
    persistSavedSearches(next);
    trackFilterSave(filters);
    toast.success(t("toast.saved"));
    setSaveDialog({ open: false, name: "" });
  };

  const handleRemove = (id) => {
    const next = saved.filter((s) => s.id !== id);
    setSaved(next);
    persistSavedSearches(next);
    toast.success(t("toast.removed"));
  };

  const handleApply = (s) => {
    setFilters({ ...s.filters });
    trackFilterLoad(s.filters);
  };

  const jsonLd = React.useMemo(() => [
    breadcrumbLd([{ name: "Home", url: "/" }, { name: "Properties", url: "/properties" }]),
    properties.length ? itemListLd(properties) : null,
  ].filter(Boolean), [properties]);

  useSEO({
    title: "Browse Warehouses & Industrial Properties for Rent — WarehousesUAE Dubai",
    description: "Browse live inventory of warehouses, industrial units, industrial plots, cold storage, showrooms, staff accommodation and logistics spaces for rent across Dubai. Filter by zone, type, size and rent.",
    canonical: window.location.origin + "/properties",
    image: window.location.origin + "/og-default.png",
    jsonLd,
  });

  return (
    <div data-testid="properties-page" className="min-h-screen bg-zinc-50">
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("properties.hero_kicker")}</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">
            {t("properties.hero_title")}
          </h1>
          <p className="text-zinc-600 mt-2 max-w-2xl">
            {properties.length} {t("properties.results").toLowerCase()}
          </p>

          {/* Rent / Sale tabs */}
          {!aiQuery && (
            <div className="mt-6 inline-flex border border-zinc-900 bg-white" role="tablist" aria-label="Rent or sale">
              {[
                { v: "", label: t("properties.tab_all") },
                { v: "rent", label: t("properties.tab_rent") },
                { v: "sale", label: t("properties.tab_sale") },
              ].map((tab) => {
                const active = filters.rent_sale === tab.v;
                return (
                  <button
                    key={tab.v || "all"}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`tab-${tab.v || "all"}`}
                    onClick={() => setRentSale(tab.v)}
                    className={`px-5 py-2.5 font-mono-tech text-[11px] uppercase tracking-wider transition-colors
                      ${active ? "bg-zinc-900 text-[#FFCC00]" : "text-zinc-700 hover:bg-zinc-100"}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* AI search badge — visible only when /properties?ai_query=... */}
          {aiQuery && (
            <div data-testid="ai-active-banner" className="mt-6 bg-[#031833] text-white p-4 max-w-3xl border-2 border-[#FFCC00]">
              <div className="flex items-start justify-between gap-3 mb-1">
                <span className="font-mono-tech text-[10px] uppercase tracking-wider text-[#FFCC00]">// AI SMART SEARCH</span>
                <button
                  data-testid="ai-clear"
                  onClick={clearAi}
                  className="font-mono-tech text-[10px] uppercase text-zinc-300 hover:text-[#FFCC00]"
                >
                  Clear ×
                </button>
              </div>
              <p className="font-mono-tech text-xs text-zinc-300 mb-1">Query: <span className="text-white">{aiQuery}</span></p>
              {aiReason && <p className="font-mono-tech text-[11px] text-zinc-400 leading-relaxed">{aiReason}</p>}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-12 gap-6">
        {/* Filters */}
        <aside className="lg:col-span-3 space-y-4">
          {/* Saved searches */}
          <div className="bg-white border border-zinc-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-tech text-[10px] uppercase text-[#031833]">{t("saved.title")}</span>
              {saved.length > 0 && <span className="font-mono-tech text-[10px] text-zinc-400">{saved.length}</span>}
            </div>
            {saved.length === 0 ? (
              <p className="font-mono-tech text-[10px] text-zinc-500 mb-3">{t("saved.empty")}</p>
            ) : (
              <ul className="space-y-1 mb-3 max-h-44 overflow-y-auto">
                {saved.map((s) => (
                  <li key={s.id} className="group flex items-center justify-between gap-2 border-l-2 border-zinc-200 hover:border-[#031833] px-2 py-1.5">
                    <button
                      data-testid={`apply-saved-${s.id}`}
                      onClick={() => handleApply(s)}
                      className="text-left flex-1 min-w-0"
                    >
                      <div className="font-mono-tech text-xs truncate">{s.name}</div>
                      <div className="font-mono-tech text-[9px] text-zinc-500 truncate">{describeFilters(s.filters)}</div>
                    </button>
                    <button
                      data-testid={`remove-saved-${s.id}`}
                      onClick={() => handleRemove(s.id)}
                      className="font-mono-tech text-[10px] text-red-600 opacity-0 group-hover:opacity-100 hover:underline"
                      title={t("saved.remove")}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}
            <button
              data-testid="save-search-btn"
              onClick={openSaveDialog}
              disabled={!!matchingExisting || isEmpty}
              className="w-full font-mono-tech text-[10px] uppercase px-3 py-2 border border-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-900 hover:text-white"
            >
              {matchingExisting ? t("saved.saved_btn") : t("saved.save_btn")}
            </button>
          </div>

          <div className="sticky top-20 bg-white border border-zinc-200 p-5 space-y-5">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <span className="font-display text-lg tracking-tighter">{t("properties.filter_title")}</span>
              <button data-testid="reset-filters-btn" onClick={reset} className="font-mono-tech text-[10px] uppercase text-[#031833] hover:underline">{t("properties.reset")}</button>
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_search")}</label>
              <input
                data-testid="filter-search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder={t("properties.search_ph")}
                className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
              />
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_emirate")}</label>
              <select
                data-testid="filter-emirate"
                value={filters.emirate}
                onChange={(e) => setFilters({ ...filters, emirate: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
              >
                <option value="">{t("properties.all_emirates")}</option>
                {UAE_EMIRATES.map((em) => <option key={em.code} value={em.name}>{em.name}</option>)}
              </select>
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_zone")}</label>
              <select
                data-testid="filter-zone"
                value={filters.zone}
                onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
              >
                <option value="">{t("properties.all_zones")}</option>
                {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_type")}</label>
              <select
                data-testid="filter-type"
                value={filters.property_type}
                onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
              >
                <option value="">{t("properties.all_types")}</option>
                {PROPERTY_TYPES.map((pt) => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
              </select>
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_size")}</label>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="filter-min-size" type="number" value={filters.min_size} onChange={(e) => setFilters({ ...filters, min_size: e.target.value })} placeholder={t("properties.min")} className="border border-zinc-300 px-2 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
                <input data-testid="filter-max-size" type="number" value={filters.max_size} onChange={(e) => setFilters({ ...filters, max_size: e.target.value })} placeholder={t("properties.max")} className="border border-zinc-300 px-2 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              </div>
            </div>

            <div>
              <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1.5">{t("properties.label_rent")}</label>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} placeholder={t("properties.min")} className="border border-zinc-300 px-2 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
                <input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} placeholder={t("properties.max")} className="border border-zinc-300 px-2 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              </div>
            </div>
          </div>
        </aside>

        {/* Listings */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-tech text-xs text-zinc-500">
              {loading ? t("properties.loading") : `${properties.length} ${properties.length === 1 ? t("properties.result") : t("properties.results")}`}
            </div>
          </div>
          {properties.length === 0 && !loading ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
              <div className="font-display text-2xl tracking-tighter mb-2">{t("properties.no_match_title")}</div>
              <p className="text-zinc-500 text-sm mb-4">{t("properties.no_match_sub")}</p>
              <Link to="/contact" className="inline-block bg-zinc-900 text-white font-mono-tech text-xs px-4 py-2">{t("properties.request_custom")}</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          )}
        </div>
      </div>

      <Dialog open={saveDialog.open} onOpenChange={(o) => setSaveDialog((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="save-search-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tighter">{t("saved.prompt")}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="save-search-name-input"
            autoFocus
            value={saveDialog.name}
            onChange={(e) => setSaveDialog((s) => ({ ...s, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && confirmSave()}
            placeholder={t("saved.default_name")}
            className="font-mono-tech text-sm"
          />
          <DialogFooter className="gap-2">
            <button
              type="button"
              data-testid="save-search-cancel"
              onClick={() => setSaveDialog({ open: false, name: "" })}
              className="font-mono-tech text-xs uppercase px-4 py-2 border border-zinc-300 hover:bg-zinc-100"
            >
              {t("saved.cancel")}
            </button>
            <button
              type="button"
              data-testid="save-search-confirm"
              onClick={confirmSave}
              className="font-mono-tech text-xs uppercase px-4 py-2 bg-zinc-950 text-white hover:bg-[#031833]"
            >
              {t("saved.save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
